import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  CitedFileCatalog,
  CitedFilesResponse,
  FileCitation,
  EnhancedFileCitation,
} from '../../types/chat.types';

/**
 * Fetch cited files information for a list of file IDs
 */
const fetchCitedFiles = async (
  storeFileIds: string[],
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
): Promise<CitedFilesResponse> => {
  if (storeFileIds.length === 0) {
    return { status: 'ok', catalogs: [] };
  }

  try {
    const baseUrl = await discoveryApi.getBaseUrl(
      'developer-portal-support-agent',
    );

    const url = `${baseUrl}/rag-service/cited-files`;

    const response = await fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ storeFileIds }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch cited files:', error);
    throw error;
  }
};

type CitationEnhancerDeps = {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  fetcher?: typeof fetchCitedFiles;
};

type CitationEnhancer = {
  enhanceCitations: (
    citations: FileCitation[],
  ) => Promise<EnhancedFileCitation[]>;
};

const isIndexMarkdownPath = (markdownPath: string): boolean =>
  /(^|\/)index\.md$/i.test(markdownPath);

function normalizeDevportalLink(link: string): string {
  if (!link) {
    return link;
  }

  // MkDocs/TechDocs canonical URLs should not end with /index or /index/.
  return link.replace(/\/index(?:\/)?(?=$|[?#])/i, '/');
}

const mapCitationWithCatalog = (
  citation: FileCitation,
  catalog: CitedFileCatalog | null | undefined,
): EnhancedFileCitation => {
  if (!catalog) {
    return citation;
  }

  return {
    ...citation,
    devportal_link: isIndexMarkdownPath(catalog.markdown_path)
      ? normalizeDevportalLink(catalog.devportal_link)
      : catalog.devportal_link,
    document_title: catalog.document_title,
    markdown_path: catalog.markdown_path,
  };
};

const normalizeIds = (citations: FileCitation[]): string[] =>
  Array.from(new Set(citations.map(citation => citation.file_id))).filter(id =>
    id.startsWith('file-'),
  );

export function createCitationEnhancer({
  discoveryApi,
  fetchApi,
  fetcher = fetchCitedFiles,
}: CitationEnhancerDeps): CitationEnhancer {
  const catalogCache = new Map<string, CitedFileCatalog | null>();
  const inFlightById = new Map<string, Promise<void>>();

  const ensureCatalogs = async (ids: string[]) => {
    if (ids.length === 0) {
      return;
    }

    const pendingFromInFlight: Promise<void>[] = [];
    const toFetch = ids.filter(id => {
      if (catalogCache.has(id)) {
        return false;
      }

      const inFlight = inFlightById.get(id);
      if (inFlight) {
        pendingFromInFlight.push(inFlight);
        return false;
      }

      return true;
    });

    let fetchPromise: Promise<void> | null = null;

    if (toFetch.length > 0) {
      fetchPromise = (async () => {
        try {
          const citedFilesResponse = await fetcher(
            toFetch,
            discoveryApi,
            fetchApi,
          );
          const fetchedCatalogs = new Map(
            citedFilesResponse.catalogs.map(catalog => [
              catalog.store_file_id,
              catalog,
            ]),
          );

          toFetch.forEach(id => {
            catalogCache.set(id, fetchedCatalogs.get(id) ?? null);
          });
        } finally {
          toFetch.forEach(id => {
            inFlightById.delete(id);
          });
        }
      })();

      toFetch.forEach(id => {
        inFlightById.set(id, fetchPromise as Promise<void>);
      });
    }

    await Promise.all([
      ...pendingFromInFlight,
      ...(fetchPromise ? [fetchPromise] : []),
    ]);
  };

  return {
    enhanceCitations: async (citations: FileCitation[]) => {
      if (!citations || citations.length === 0) {
        return [];
      }

      const ids = normalizeIds(citations);
      await ensureCatalogs(ids);

      return citations.map(citation =>
        mapCitationWithCatalog(citation, catalogCache.get(citation.file_id)),
      );
    },
  };
}
